/**
 * DrumSoundboard — the audition view: twelve instrument cards, each with its
 * photo, names, origin, description and one pad per stroke.
 *
 * This is the original soundboard page body lifted into a component so
 * either it or the drum landing page can be mounted at that route, switched by
 * hand in `App.tsx`. It renders no page chrome of its own beyond its header,
 * so it drops into a route or into a section of another page unchanged.
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  AFRICAN_INSTRUMENTS,
  ALL_SAMPLE_URLS,
  PAD_BY_KEY,
  InstrumentPad,
} from '../../../data/africanInstruments';
import { useSamplePlayer } from '../../../hooks/useSamplePlayer';
import { usePageLoadAnimation } from '../../../hooks/useAnimation';

/** Identifies a pad across the whole board, for flash highlighting. */
const padId = (slug: string, label: string) => `${slug}:${label}`;

export interface DrumSoundboardProps {
  /**
   * Hide the links out to the challenges and leaderboard. Useful when the
   * board is embedded in a page that already offers them.
   */
  hideNav?: boolean;
}

export const DrumSoundboard: React.FC<DrumSoundboardProps> = ({
  hideNav = false,
}) => {
  usePageLoadAnimation();

  const { load, play, isReady, isLoading, loadedCount, totalCount, error } =
    useSamplePlayer(ALL_SAMPLE_URLS);

  const [activePad, setActivePad] = useState<string | null>(null);
  const flashTimerRef = useRef<number>();

  const trigger = useCallback(
    (slug: string, pad: InstrumentPad) => {
      play(pad.src, pad.pitchRatio);

      setActivePad(padId(slug, pad.label));
      window.clearTimeout(flashTimerRef.current);
      flashTimerRef.current = window.setTimeout(() => setActivePad(null), 180);
    },
    [play],
  );

  /**
   * Samples decode on the first interaction — browsers block audio until
   * a user gesture, so there is nothing to gain by loading sooner.
   */
  const handlePad = useCallback(
    async (slug: string, pad: InstrumentPad) => {
      if (!isReady) {
        await load();
      }
      trigger(slug, pad);
    },
    [isReady, load, trigger],
  );

  // Keyboard bindings, so the whole board is playable without a mouse.
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.repeat || event.metaKey || event.ctrlKey || event.altKey) return;

      const target = event.target as HTMLElement | null;
      if (target && /^(INPUT|TEXTAREA|SELECT)$/.test(target.tagName)) return;

      const match = PAD_BY_KEY.get(event.key.toLowerCase());
      if (!match) return;

      event.preventDefault();
      void handlePad(match.instrument, match.pad);
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [handlePad]);

  useEffect(() => () => window.clearTimeout(flashTimerRef.current), []);

  return (
    <div className="min-h-screen bg-studio-950 px-6 lg:px-16 py-12">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <header className="mb-12 max-w-3xl">
          <p className="text-amber-500 font-mono text-xs tracking-[0.2em] uppercase mb-3">
            Sound Library
          </p>
          <h1 className="font-display text-display-lg font-bold text-stone-100 mb-4">
            African Drums
            <span className="text-amber-500 italic"> 非洲传统乐器</span>
          </h1>
          <p className="text-stone-400 leading-relaxed">
            Twelve traditional instruments from across the continent. Tap a pad — or press its
            key — to hear it. Drums with more than one voice have a pad per stroke.
          </p>
          <p className="text-stone-600 text-sm mt-3">
            Sounds are soundfont approximations chosen to suggest each instrument's character,
            not field recordings.
          </p>

          {/* Ready to be tested against the clock? */}
          {!hideNav && (
            <div className="flex flex-wrap items-center gap-3 mt-6">
              <Link to="/drum-challenges" className="btn-primary">
                Play Challenges
              </Link>
              <Link to="/drum-leaderboard" className="btn-secondary">
                Drum Leaderboard
              </Link>
            </div>
          )}

          {/* Load state */}
          <div className="mt-6 h-6" aria-live="polite">
            {isLoading && (
              <p className="text-stone-500 text-sm font-mono">
                Loading samples… {loadedCount}/{totalCount}
              </p>
            )}
            {error && (
              <p className="text-coral-400 text-sm font-mono">
                Couldn't load samples: {error}
              </p>
            )}
            {isReady && !error && (
              <p className="text-sage-400 text-sm font-mono">
                {totalCount} samples ready
              </p>
            )}
          </div>
        </header>

        {/* Instrument grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
          {AFRICAN_INSTRUMENTS.map((instrument) => (
            <article
              key={instrument.slug}
              className="flex flex-col bg-studio-800 border border-studio-700 rounded-2xl overflow-hidden shadow-card hover:shadow-card-hover transition-shadow duration-300"
            >
              <div className="aspect-[4/3] bg-studio-850 overflow-hidden">
                <img
                  src={instrument.image}
                  alt={`${instrument.nameEn} — ${instrument.nameZh}`}
                  loading="lazy"
                  className="w-full h-full object-cover"
                />
              </div>

              <div className="flex flex-col flex-1 p-6">
                <h2 className="font-display text-2xl font-bold text-stone-100">
                  {instrument.nameEn}
                </h2>
                <p className="text-amber-500 text-sm font-semibold mt-0.5">
                  {instrument.nameZh}
                </p>

                <p className="text-stone-500 text-xs font-mono mt-3">
                  {instrument.originEn}
                </p>
                <p className="text-stone-600 text-xs mt-1">{instrument.originZh}</p>

                <p className="text-stone-400 text-sm leading-relaxed mt-4 flex-1">
                  {instrument.descriptionEn}
                </p>
                <p className="text-stone-600 text-xs leading-relaxed mt-2">
                  {instrument.descriptionZh}
                </p>

                {/* Pads */}
                <div className="flex flex-wrap gap-2 mt-6">
                  {instrument.pads.map((pad) => {
                    const isActive = activePad === padId(instrument.slug, pad.label);
                    return (
                      <button
                        key={pad.label}
                        type="button"
                        onPointerDown={(event) => {
                          // Matches DrumPad: a drum should sound the moment the
                          // finger lands, not when it lifts. `onClick` fires on
                          // release on touch, which reads as mushy on a
                          // percussion instrument.
                          event.preventDefault();
                          void handlePad(instrument.slug, pad);
                        }}
                        aria-label={`Play ${instrument.nameEn} ${pad.label}`}
                        style={{ touchAction: 'manipulation' }}
                        className={`group touch-target no-select no-tap-highlight flex-1 min-w-[5.5rem] px-3 py-3 rounded-xl border text-left transition-all duration-150 active:scale-[0.97] ${
                          isActive
                            ? 'bg-amber-500 border-amber-400 shadow-glow-amber scale-[0.97]'
                            : 'bg-studio-750 border-studio-600 hover:border-amber-500/60 hover:bg-studio-700'
                        }`}
                      >
                        <span
                          className={`block text-sm font-bold ${
                            isActive ? 'text-studio-950' : 'text-stone-200'
                          }`}
                        >
                          {pad.label}
                        </span>
                        <span
                          className={`block text-xs ${
                            isActive ? 'text-studio-900' : 'text-stone-500'
                          }`}
                        >
                          {pad.labelZh}
                        </span>
                        <span
                          className={`hidden sm:block text-[10px] font-mono mt-1 uppercase ${
                            isActive ? 'text-studio-900' : 'text-stone-600'
                          }`}
                        >
                          key {pad.keyBinding}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>
            </article>
          ))}
        </div>
      </div>
    </div>
  );
};
